import { BanknotesIcon, CurrencyEuroIcon } from "@heroicons/react/24/solid";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import type { ClaimWithRelations } from "../api/use-claim";
import { applyCommission, formatCentsAsEuros } from "../lib/money";

/**
 * Port of ClaimRequests.vue — the dinero.js arithmetic is replaced by
 * cents-based integer math (expense prices are stored in cents).
 * @TODO Computation on API's side with currency exchange, at the rate of the Flight
 */
export function ClaimRequests({ claim }: { claim: ClaimWithRelations }) {
  const ec261CompensationAmount = claim.legalSpecification.ec261CompensationAmount;
  const ec261Cents = ec261CompensationAmount * 100;

  const passengersDetails = claim.usersClaims.map(
    ({ user, montrealConventionCompensationAmount, expenses }) => {
      const expensesCents = expenses.map(({ expense }) => Number.parseInt(expense.price, 10));
      const totalExpensesCents = expensesCents.reduce((acc, cents) => acc + cents, 0);
      const montrealCents =
        Number.parseInt((montrealConventionCompensationAmount as unknown as string) || "0", 10) * 100;

      return {
        fullName: `${user.firstName} ${user.lastName?.toUpperCase() ?? ""}`,
        birthDate: user.birthdate,
        montrealCents,
        hasExpenses: expensesCents.length > 0,
        totalExpensesCents, // TODO: Currency conversion
        totalAfterCommissionCents: applyCommission(
          totalExpensesCents + montrealCents + ec261Cents,
          claim.legalSpecification.b2cCommissionPercentageForUpclaim +
            claim.legalSpecification.b2cCommissionPercentageForTravelAgency,
        ),
      };
    },
  );

  const companyName = claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany?.displayName;
  const companyExpensesCents = companyName
    ? claim.claimExpenses
        .filter(({ paidBy }) => paidBy === "BUSINESS_TRAVEL_COMPANY")
        .reduce((acc, { expense }) => acc + Number.parseInt(expense.price, 10), 0)
    : 0;
  const companyExpenses = {
    name: companyName,
    totalExpensesCents: companyExpensesCents,
    totalAfterCommissionCents: applyCommission(
      companyExpensesCents,
      claim.legalSpecification.b2bCommissionPercentageForUpclaim +
        claim.legalSpecification.b2bCommissionPercentageForTravelAgency,
    ),
    hasExpenses: Boolean(companyName) && companyExpensesCents > 0,
  };

  const travelAgencyExpensesCents = claim.claimExpenses
    .filter(({ paidBy }) => paidBy === "TRAVEL_AGENCY")
    .reduce((acc, { expense }) => acc + Number.parseInt(expense.price, 10), 0);
  const travelAgencyExpenses = {
    totalExpensesCents: travelAgencyExpensesCents,
    totalAfterCommissionCents: applyCommission(
      travelAgencyExpensesCents,
      claim.legalSpecification.travelAgencyLossCommissionPercentageForUpclaim,
    ),
    hasExpenses: claim.claimExpenses.some(({ paidBy }) => paidBy === "TRAVEL_AGENCY"),
  };

  const totalCents =
    passengersDetails.reduce((acc, { totalExpensesCents, montrealCents }) => {
      return acc + totalExpensesCents + montrealCents;
    }, 0) +
    ec261Cents * claim.usersClaims.length +
    companyExpenses.totalExpensesCents +
    travelAgencyExpenses.totalExpensesCents;

  const totalAfterCommissionCents =
    passengersDetails.reduce((acc, { totalAfterCommissionCents }) => {
      return acc + totalAfterCommissionCents;
    }, 0) +
    companyExpenses.totalAfterCommissionCents +
    travelAgencyExpenses.totalAfterCommissionCents;

  return (
    <div className="py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
      <dt className="text-sm leading-6 font-medium text-gray-700">
        <span className="inline-flex">
          <BanknotesIcon className="mr-2 h-5 w-5 text-slate-400" />
          Demandes
        </span>
      </dt>
      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
        {passengersDetails.map((item) => (
          <Tooltip key={item.fullName}>
            <TooltipTrigger render={<ul className="list-disc" />}>
              <li className="mb-2">
                {item.fullName}
                <ul className="list-inside list-disc">
                  <li>
                    <span className="font-bold">{ec261CompensationAmount}€</span> de Règlement CE261/2004
                  </li>
                  {item.montrealCents !== 0 ? (
                    <li>
                      <span className="font-bold">{formatCentsAsEuros(item.montrealCents)}€</span> de
                      Convention de Montréal 1999
                    </li>
                  ) : null}
                  {item.hasExpenses ? (
                    <li>
                      <span className="font-bold">{formatCentsAsEuros(item.totalExpensesCents)}€</span> de
                      frais supplémentaires
                    </li>
                  ) : null}
                  {item.birthDate ? (
                    <li>
                      Né(e) le{" "}
                      <span className="font-bold">{new Date(item.birthDate).toLocaleDateString()}</span>
                    </li>
                  ) : null}
                  <li className="ml-4 list-none text-gray-500">
                    Après commission :{" "}
                    <span className="font-semibold">
                      {formatCentsAsEuros(item.totalAfterCommissionCents)}€
                    </span>
                  </li>
                </ul>
              </li>
            </TooltipTrigger>
            <TooltipContent side="left">
              Après commission : {formatCentsAsEuros(item.totalAfterCommissionCents)} €
            </TooltipContent>
          </Tooltip>
        ))}
        {companyExpenses.hasExpenses ? (
          <Tooltip>
            <TooltipTrigger render={<ul className="mt-6 list-disc" />}>
              <li>
                <span className="inline-flex align-middle">{companyExpenses.name} (Entreprise)</span>
                <ul className="list-inside list-disc">
                  <li>
                    <span className="font-bold">
                      {formatCentsAsEuros(companyExpenses.totalExpensesCents)}€
                    </span>{" "}
                    de frais supplémentaires
                  </li>
                  <li className="ml-4 list-none text-gray-500">
                    Après commission :{" "}
                    <span className="font-semibold">
                      {formatCentsAsEuros(companyExpenses.totalAfterCommissionCents)}€
                    </span>
                  </li>
                </ul>
              </li>
            </TooltipTrigger>
            <TooltipContent side="left">
              Après commission : {formatCentsAsEuros(companyExpenses.totalAfterCommissionCents)} €
            </TooltipContent>
          </Tooltip>
        ) : null}
        {travelAgencyExpenses.hasExpenses ? (
          <Tooltip>
            <TooltipTrigger render={<ul className="mt-6 list-disc" />}>
              <li>
                <span className="inline-flex align-middle">Pour l'Agence de voyages</span>
                <ul className="list-inside list-disc">
                  <li>
                    <span className="font-bold">
                      {formatCentsAsEuros(travelAgencyExpenses.totalExpensesCents)}€
                    </span>{" "}
                    de demandes
                  </li>
                  <li className="ml-4 list-none text-gray-500">
                    Après commission :{" "}
                    <span className="font-semibold">
                      {formatCentsAsEuros(travelAgencyExpenses.totalAfterCommissionCents)}€
                    </span>
                  </li>
                </ul>
              </li>
            </TooltipTrigger>
            <TooltipContent side="left">
              Après commission : {formatCentsAsEuros(travelAgencyExpenses.totalAfterCommissionCents)} €
            </TooltipContent>
          </Tooltip>
        ) : null}
      </dd>
      <dt className="border-t border-dashed pt-5 text-sm leading-6 font-medium text-gray-700">
        <span className="inline-flex">
          <CurrencyEuroIcon className="mr-2 h-5 w-5 text-slate-400" />
          Montant total
        </span>
      </dt>
      <dd className="mt-1 border-t border-dashed pt-5 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {formatCentsAsEuros(totalCents)}€{" "}
        <span>
          (Après commission:{" "}
          <span className="font-semibold">{formatCentsAsEuros(totalAfterCommissionCents)}€</span>)
        </span>
      </dd>
    </div>
  );
}
